import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { rejectBody } from '../../utils/apiError';
import { withBranch } from '../../utils/branchRequest';

const API_URL = '/api/job-cards';

export const fetchJobCards = createAsyncThunk('jobCards/fetchAll', async (params, { rejectWithValue }) => {
  try {
    const response = await axios.get(API_URL, { params });
    return response.data;

  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
});

export const fetchJobCard = createAsyncThunk('jobCards/fetchOne', async (id, { rejectWithValue }) => {
  try {
    const response = await axios.get(`${API_URL}/${id}`, { timeout: 30000 });
    return response.data;

  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
});

export const createJobCard = createAsyncThunk(
  'jobCards/create',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_URL, data);
      return response.data;
    } catch (error) {
      // Return the full error body so callers can detect the open-job-card
      // conflict (409, code: OPEN_JOB_CARD_EXISTS) and offer "create anyway".
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

export const updateJobCard = createAsyncThunk('jobCards/update', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await axios.put(`${API_URL}/${id}`, data);
    return response.data;

  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
});

export const updateJobStatus = createAsyncThunk('jobCards/updateStatus', async ({ id, status, notes }, { rejectWithValue }) => {
  try {
      const response = await axios.patch(`${API_URL}/${id}/status`, { status, notes });
      return response.data;
  
  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
}
);

export const assignMechanic = createAsyncThunk('jobCards/assign', async ({ id, mechanic_id }, { rejectWithValue }) => {
  try {
    const response = await axios.patch(`${API_URL}/${id}/assign`, { mechanic_id });
    return response.data;

  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
});

export const createWalkInJobCard = createAsyncThunk(
  'jobCards/createWalkIn',
  async (data, { rejectWithValue }) => {
    try {
      // P2: __branchId pins the create to one branch (stripped from the body).
      const { __branchId, ...body } = data || {};
      const response = __branchId
        ? await axios.post(`${API_URL}/walk-in`, body, withBranch(__branchId))
        : await axios.post(`${API_URL}/walk-in`, body);
      return response.data;
    } catch (error) {
      // Full error body so callers can detect OPEN_JOB_CARD_EXISTS (409).
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

export const completeJobCard = createAsyncThunk(
  'jobCards/complete',
  async ({ id, customer_id, vehicle_updates }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(`${API_URL}/${id}/complete`, {
        customer_id,
        vehicle_updates,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(rejectBody(error));
    }
  }
);

const jobCardSlice = createSlice({
  name: 'jobCards',
  initialState: {
    items: [],
    currentJobCard: null,
    loading: false,
    error: null,
    pagination: null,
    // requestId of the newest fetchJobCard read. Anything older is a response
    // for a card the user has already left — see fetchJobCard.fulfilled.
    currentJobCardRequestId: null,
  },
  reducers: {
    clearCurrentJobCard: (state) => {
      state.currentJobCard = null;
      state.currentJobCardRequestId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchJobCards.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchJobCards.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchJobCards.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || action.payload?.message || action.error.message;
      })
      // Route-response ownership (PERF-API-01 P02, section16.17).
      //
      // This used to accept whatever arrived. Opening card A on a slow link and
      // navigating to B before A answered left A's job in `currentJobCard` — B's
      // page then rendered A's customer, vehicle, status and money panels under
      // B's URL. Nothing about that is cross-tenant (the server still authorized
      // every read), but it is the wrong card in front of the operator.
      //
      // Only the NEWEST read may write. Navigation dispatches a new
      // fetchJobCard, whose pending re-stamps the id below, so the older answer
      // is dropped. Two concurrent reads of the SAME card resolve the same way —
      // the later request wins, which is the fresher of the two anyway.
      .addCase(fetchJobCard.pending, (state, action) => {
        state.currentJobCardRequestId = action.meta.requestId;
      })
      .addCase(fetchJobCard.fulfilled, (state, action) => {
        if (state.currentJobCardRequestId !== action.meta.requestId) return;
        state.currentJobCardRequestId = null;
        state.currentJobCard = action.payload.data;
      })
      .addCase(createJobCard.fulfilled, (state, action) => {
        state.items.unshift(action.payload.data);
      })
      .addCase(createWalkInJobCard.fulfilled, (state, action) => {
        state.items.unshift(action.payload.data);
      })
      .addCase(completeJobCard.fulfilled, (state, action) => {
        const index = state.items.findIndex((item) => item.id === action.payload.data.id);
        if (index !== -1) {
          state.items[index] = action.payload.data;
        }
        if (state.currentJobCard?.id === action.payload.data.id) {
          state.currentJobCard = action.payload.data;
        }
      })
      .addCase(updateJobCard.fulfilled, (state, action) => {
        const index = state.items.findIndex((item) => item.id === action.payload.data.id);
        if (index !== -1) {
          state.items[index] = action.payload.data;
        }
        if (state.currentJobCard?.id === action.payload.data.id) {
          state.currentJobCard = action.payload.data;
        }
      });
  },
});

export const { clearCurrentJobCard } = jobCardSlice.actions;
export default jobCardSlice.reducer;
